import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, post } from '../api.js';
import type { WorkspaceSnapshot } from '../types.js';
import { useI18n } from '../i18n.js';

export function WorkspacePage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [snapshot, setSnapshot] = useState<WorkspaceSnapshot | null>(null);
  const [error, setError] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const refreshTimer = useRef<number | undefined>(undefined);
  const load = useCallback(async () => {
    if (!id) return;
    try {
      setSnapshot(await api<WorkspaceSnapshot>(`/workspaces/${id}/snapshot`));
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load workspace');
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!id) return;
    const events = new EventSource(`/api/v1/workspaces/${id}/events`);
    const handleEvent = () => {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(() => void load(), 200);
    };
    events.addEventListener('uaw', handleEvent);
    events.onerror = () => { /* EventSource reconnects and resumes with Last-Event-ID. */ };
    return () => { events.removeEventListener('uaw', handleEvent); events.close(); window.clearTimeout(refreshTimer.current); };
  }, [id, load]);

  const createRepository = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      await post(`/workspaces/${id}/repositories`, {
        display_name: data.get('display_name'),
        remote_fingerprint: data.get('remote_fingerprint') || null,
        ignore_case: false
      });
      form.reset();
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Failed'); }
  };

  const createSetupCode = async (repositoryId: string) => {
    try {
      const result = await post<{ setup_code: string }>(`/workspaces/${id}/setup-codes`, {
        repository_id: repositoryId,
        expires_in_minutes: 10
      });
      setSetupCode(result.setup_code);
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Failed'); }
  };

  if (!snapshot) return <div className="content-width"><p>{t('loading')}</p>{error && <p className="error-box">{error}</p>}</div>;
  const critical = snapshot.overlaps.filter((overlap) => overlap.severity === 'critical').length;
  const active = snapshot.sessions.filter((session) => session.state === 'active').length;
  return (
    <div className="content-width">
      <div className="breadcrumbs"><Link to="/workspaces">{t('workspaces')}</Link><span>/</span><span>{snapshot.workspace.name}</span></div>
      <div className="page-heading"><div><span className="eyebrow">Workspace</span><h1>{snapshot.workspace.name}</h1><p>Последовательность событий: {snapshot.workspace.event_sequence}</p></div><span className="live-pill"><i /> {t('online')}</span></div>
      {error && <p className="error-box" role="alert">{error}</p>}
      <section className="metric-grid" aria-label="Workspace summary">
        <Metric label={t('sessions')} value={active} hint={`${snapshot.sessions.length} всего`} />
        <Metric label={t('workItems')} value={snapshot.work_items.filter((item) => item.status === 'active').length} hint={`${snapshot.work_items.length} всего`} />
        <Metric label={t('overlaps')} value={snapshot.overlaps.length} hint={`${critical} критических`} danger={critical > 0} />
        <Metric label={t('repositories')} value={snapshot.repositories.length} hint={`Policy v${snapshot.workspace.policy_version}`} />
      </section>

      <div className="dashboard-grid">
        <section className="panel span-2">
          <div className="section-heading"><div><span className="eyebrow">Live</span><h2>{t('workItems')}</h2></div></div>
          {snapshot.work_items.length === 0 ? <p className="muted">{t('empty')}</p> : <div className="table-wrap"><table><thead><tr><th>Работа</th><th>Статус</th><th>Намерение</th><th>Обновлено</th></tr></thead><tbody>{snapshot.work_items.slice(0, 20).map((item) => <tr key={item.id}><td><Link to={`/work-items/${item.id}`}><strong>{item.title}</strong></Link></td><td><Status value={item.status} /></td><td>{item.goal ?? <span className="muted">{t('noIntent')}</span>}</td><td>{relativeTime(item.updated_at)}</td></tr>)}</tbody></table></div>}
        </section>
        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">Presence</span><h2>{t('sessions')}</h2></div></div>
          <div className="stack-list">{snapshot.sessions.length === 0 ? <p className="muted">{t('empty')}</p> : snapshot.sessions.map((session) => <div className="agent-row" key={session.id}><span className={`presence ${session.state}`} /><div><strong>{session.harness_name}</strong><small>{session.model ?? session.integration_level} · {session.branch ?? 'detached'}</small></div><Status value={session.coordination_state} /></div>)}</div>
        </section>
      </div>

      <section className="panel">
        <div className="section-heading"><div><span className="eyebrow">Coordination</span><h2>{t('overlaps')}</h2></div></div>
        {snapshot.overlaps.length === 0 ? <div className="success-empty">Нет активных пересечений</div> : <div className="incident-grid">{snapshot.overlaps.map((overlap) => <Link className={`incident-card severity-${overlap.severity}`} to={`/overlaps/${overlap.id}`} key={overlap.id}><div><span className="severity-label">{overlap.severity}</span><h3>{overlap.resource_key}</h3><p>{overlap.rule_id} · {overlap.parties.length} стороны</p></div><span>→</span></Link>)}</div>}
      </section>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">Git bindings</span><h2>{t('repositories')}</h2></div></div>
          <div className="stack-list">{snapshot.repositories.map((repository) => <div className="repo-row" key={repository.id}><div><strong>{repository.display_name}</strong><small>{repository.remote_fingerprint ?? 'Без remote fingerprint'}</small></div><button className="button secondary small" onClick={() => void createSetupCode(repository.id)}>Код подключения</button></div>)}</div>
          <form className="vertical-form compact" onSubmit={(event) => void createRepository(event)}><label>Название<input name="display_name" required maxLength={200} placeholder="my-project" /></label><label>Git remote fingerprint<input name="remote_fingerprint" maxLength={1024} placeholder="необязательно" /></label><button className="button primary">Добавить репозиторий</button></form>
          {setupCode && <div className="secret-box"><strong>Одноразовый код · 10 минут</strong><code>{setupCode}</code><button className="button ghost small" onClick={() => void navigator.clipboard.writeText(setupCode)}>Копировать</button></div>}
        </section>
        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">Audit-friendly</span><h2>{t('activity')}</h2></div></div>
          <ol className="timeline">{snapshot.events.slice(-12).reverse().map((event) => <li key={event.id}><span className="timeline-dot" /><div><strong>{humanizeEvent(event.event_type)}</strong><small>#{event.workspace_sequence} · {relativeTime(event.occurred_at)}</small></div></li>)}</ol>
        </section>
      </div>
    </div>
  );
}

function Metric({ label, value, hint, danger = false }: { label: string; value: number; hint: string; danger?: boolean }) { return <article className={`metric-card ${danger ? 'danger' : ''}`}><span>{label}</span><strong>{value}</strong><small>{hint}</small></article>; }
export function Status({ value }: { value: string }) { return <span className={`status status-${value}`}>{value.replaceAll('_', ' ')}</span>; }
export function relativeTime(value: string) { const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000); if (seconds < 60) return `${seconds}с назад`; if (seconds < 3600) return `${Math.round(seconds / 60)}м назад`; if (seconds < 86400) return `${Math.round(seconds / 3600)}ч назад`; return new Date(value).toLocaleDateString(); }
function humanizeEvent(value: string) { return value.replaceAll('_', ' ').replaceAll('.', ' → '); }
